import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './base.api';
import {
	SustainabilityFormDto,
	SustainabilityFormSaveRequestDto,
	SustainabilityFormUpdateRequestDto,
	SustainabilityFormDisableEnableRequestDto,
	SustainabilityFormSendForApprovalRequestDto,
	SustainabilityFormSendForApprovalResponseDto,
	SustainabilityFormDifferenceCheckDto,
	SustainabilityFormApproveRequestDto,
	SustainabilityFormRejectRequestDto,
	SustainabilityFormCancelWaitingForApprovalRequestDto,
	SustainabilityQuestionDto,
	SustainabilitySectionDto,
	SustainabilityFormSectionSupportingDocumentDto,
	SustainabilityFormSupportingDocumentFilenameUpdateRequestDto,
	SustainabilityFormByAccessTokenDto,
	SustainabilityFormSectionApplicabilityUpdateRequestDto,
	SustainabilityFormApprovalRequestDto,
	QuestionAnswerDto
} from '../../models/sustainability';
import HttpClient from '../../services/http-client/http-client';

const baseUrl = `${process.env.REACT_APP_PROFILE_URL}/api/v2/sustainability`;

export const sustainabilityApi = createApi({
	reducerPath: 'sustainabilityApi',
	baseQuery: axiosBaseQuery(baseUrl),
	// Tags identify cached data so we can invalidate it: when a mutation invalidates a tag,
	// any query that provides that tag will refetch. Keeps the UI in sync after create/update/delete.
	tagTypes: ['sustainability'],
	endpoints: (builder) => ({
		getQuestions: builder.query<
			SustainabilityQuestionDto[],
			{ profileType?: number; category?: number } | void
		>({
			query: (params = {}) => ({
				url: '/questions',
				method: 'GET',
				params: params || undefined
			}),
			providesTags: [{ type: 'sustainability', id: 'QUESTIONS' }]
		}),

		getQuestionsBySection: builder.query<
			SustainabilityQuestionDto[],
			{ section: string; profileType: number }
		>({
			query: ({ section, profileType }) => ({
				url: `/questions/section`,
				method: 'GET',
				params: { section, profileType }
			}),
			providesTags: (_result, _error, { section, profileType }) => [
				{ type: 'sustainability', id: 'QUESTIONS' },
				{ type: 'sustainability', id: `QUESTIONS-${section}-${profileType}` }
			]
		}),

		getQuestionById: builder.query<SustainabilityQuestionDto, { questionId: number }>({
			query: ({ questionId }) => ({
				url: `/questions/${questionId}`,
				method: 'GET'
			}),
			providesTags: (_result, _error, { questionId }) => [
				{ type: 'sustainability', id: 'QUESTIONS' },
				{ type: 'sustainability', id: `QUESTION-${questionId}` }
			]
		}),

		getCertificateQuestion: builder.query<QuestionAnswerDto, { profileId: number; formId: number }>(
			{
				query: ({ profileId, formId }) => ({
					url: `/${profileId}/form/${formId}/certificateQuestion`,
					method: 'GET'
				}),
				providesTags: (_result, _error, { profileId, formId }) => [
					{ type: 'sustainability', id: profileId },
					{ type: 'sustainability', id: formId }
				]
			}
		),

		createForm: builder.mutation<
			SustainabilityFormDto,
			{ profileId: number; request: SustainabilityFormSaveRequestDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/form`,
				method: 'POST',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		updateForm: builder.mutation<
			SustainabilityFormDto,
			{ profileId: number; request: SustainabilityFormUpdateRequestDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/form`,
				method: 'PUT',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		updateSectionApplicability: builder.mutation<
			void,
			{
				profileId: number;
				formId: number;
				request: SustainabilityFormSectionApplicabilityUpdateRequestDto;
			}
		>({
			query: ({ profileId, formId, request }) => ({
				url: `/${profileId}/form/${formId}/section-applicability`,
				method: 'PUT',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		uploadSupportingDocument: builder.mutation<
			SustainabilityFormSectionSupportingDocumentDto,
			{
				profileId: number;
				formId: number;
				section: string;
				file: File;
				signal?: AbortSignal;
				onProgress?: (percent: number) => void;
			}
		>({
			queryFn: async (arg) => {
				const { profileId, formId, section, file, signal, onProgress } = arg;

				const formData = new FormData();

				formData.append('file', file);

				const url = `/${profileId}/form/${formId}/supportingDocument?section=${encodeURIComponent(
					section
				)}`;

				try {
					const result = await HttpClient.post(
						baseUrl,
						url,
						formData,
						{ 'Content-Type': 'multipart/form-data' },
						{
							onUploadProgress: onProgress
								? (e: ProgressEvent) => {
										if (e.total) onProgress((e.loaded / e.total) * 100);
									}
								: undefined,
							signal
						}
					);
					return { data: result.data as SustainabilityFormSectionSupportingDocumentDto };
				} catch (error) {
					return { error: error as { status?: number; data?: unknown } };
				}
			},
			invalidatesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		removeSupportingDocument: builder.mutation<
			void,
			{ profileId: number; formId: number; documentId: number }
		>({
			query: ({ profileId, formId, documentId }) => ({
				url: `/${profileId}/form/${formId}/supportingDocument/${documentId}`,
				method: 'DELETE'
			}),
			invalidatesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		updateSupportingDocumentFilename: builder.mutation<
			SustainabilityFormSectionSupportingDocumentDto,
			{
				profileId: number;
				request: SustainabilityFormSupportingDocumentFilenameUpdateRequestDto;
			}
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/form/supportingDocument/filename`,
				method: 'PUT',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId, request }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: request.formId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getForm: builder.query<SustainabilityFormDto, { profileId: number; formId: number }>({
			query: ({ profileId, formId }) => ({
				url: `/${profileId}/form`,
				method: 'GET',
				params: { formId }
			}),
			providesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId }
			]
		}),

		getFormsByProfileId: builder.query<SustainabilityFormDto[], number>({
			query: (profileId) => ({
				url: `/${profileId}/forms`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getLatestApprovedForm: builder.query<SustainabilityFormDto | null, number>({
			query: (profileId) => ({
				url: `/${profileId}/form/latest/approved`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getLatestInProgressForm: builder.query<SustainabilityFormDto | null, number>({
			query: (profileId) => ({
				url: `/${profileId}/form/latest/in-progress`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getLatestForm: builder.query<SustainabilityFormDto | null, number>({
			query: (profileId) => ({
				url: `/${profileId}/form/latest/any`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getLatestDeclinedForm: builder.query<SustainabilityFormDto | null, number>({
			query: (profileId) => ({
				url: `/${profileId}/form/latest/rejected`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		setFormDisabled: builder.mutation<
			void,
			{ profileId: number; request: SustainabilityFormDisableEnableRequestDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/form/disable`,
				method: 'POST',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		sendFormForApproval: builder.mutation<
			SustainabilityFormSendForApprovalResponseDto,
			{ profileId: number; request: SustainabilityFormSendForApprovalRequestDto }
		>({
			query: ({ profileId, request }) => ({
				url: `/${profileId}/form/send-for-approval`,
				method: 'POST',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getSubmittedApprovalRequest: builder.query<
			SustainabilityFormApprovalRequestDto,
			{ profileId: number; formId: number }
		>({
			query: ({ profileId, formId }) => ({
				url: `/${profileId}/form/${formId}/approval-request`,
				method: 'GET'
			}),
			providesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId }
			]
		}),

		resendFormForApproval: builder.mutation<
			SustainabilityFormSendForApprovalResponseDto,
			{ profileId: number; formId: number; request: SustainabilityFormSendForApprovalRequestDto }
		>({
			query: ({ profileId, formId, request }) => ({
				url: `/${profileId}/form/${formId}/resend-for-approval`,
				method: 'POST',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		cloneForm: builder.mutation<SustainabilityFormDto, { profileId: number; formId: number }>({
			query: ({ profileId, formId }) => ({
				url: `/${profileId}/form/${formId}/clone`,
				method: 'POST'
			}),
			invalidatesTags: (_result, _error, { profileId, formId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: formId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		cancelFormWaitingForApproval: builder.mutation<
			void,
			{
				profileId: number;
				formId: number;
				request: SustainabilityFormCancelWaitingForApprovalRequestDto;
			}
		>({
			query: ({ profileId, formId, request }) => ({
				url: `/${profileId}/form/${formId}/cancel-waiting-for-approval`,
				method: 'POST',
				data: request
			}),
			invalidatesTags: (_result, _error, { profileId }) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		approveForm: builder.mutation<
			void,
			{ accessToken: string; request: SustainabilityFormApproveRequestDto }
		>({
			query: ({ accessToken, request }) => ({
				url: '/form/approve',
				method: 'POST',
				params: { accessToken },
				data: request
			})
		}),

		rejectForm: builder.mutation<
			void,
			{ accessToken: string; request: SustainabilityFormRejectRequestDto }
		>({
			query: ({ accessToken, request }) => ({
				url: '/form/reject',
				method: 'POST',
				params: { accessToken },
				data: request
			})
		}),

		softDeleteFormsByProfileId: builder.mutation<void, number>({
			query: (profileId) => ({
				url: `/${profileId}/forms`,
				method: 'DELETE'
			}),
			invalidatesTags: (_result, _error, profileId) => [
				{ type: 'sustainability', id: profileId },
				{ type: 'sustainability', id: 'LIST' }
			]
		}),

		getDifferenceCheck: builder.query<SustainabilityFormDifferenceCheckDto, number>({
			query: (profileId) => ({
				url: `/${profileId}/form/difference-check`,
				method: 'GET'
			}),
			providesTags: (_result, _error, profileId) => [{ type: 'sustainability', id: profileId }]
		}),

		getProfileSections: builder.query<SustainabilitySectionDto[], number>({
			query: (profileType) => ({
				url: '/questions/sections',
				method: 'GET',
				params: { profileType }
			}),
			providesTags: (_result, _error, profileType) => [
				{ type: 'sustainability', id: profileType },
				{ type: 'sustainability', id: 'SECTIONS' }
			]
		}),

		getFormFromToken: builder.query<SustainabilityFormByAccessTokenDto, string>({
			query: (token) => ({
				url: `/form/accessToken/${token}`,
				method: 'GET'
			})
		})
	})
});

export const {
	useGetQuestionsQuery,
	useGetQuestionsBySectionQuery,
	useGetQuestionByIdQuery,
	useLazyGetQuestionByIdQuery,
	useGetCertificateQuestionQuery,
	useGetProfileSectionsQuery,
	useCreateFormMutation,
	useUpdateFormMutation,
	useCloneFormMutation,
	useUpdateSectionApplicabilityMutation,
	useUploadSupportingDocumentMutation,
	useRemoveSupportingDocumentMutation,
	useUpdateSupportingDocumentFilenameMutation,
	useGetFormQuery,
	useGetFormsByProfileIdQuery,
	useGetLatestApprovedFormQuery,
	useGetLatestInProgressFormQuery,
	useGetLatestDeclinedFormQuery,
	useSetFormDisabledMutation,
	useSendFormForApprovalMutation,
	useGetSubmittedApprovalRequestQuery,
	useResendFormForApprovalMutation,
	useCancelFormWaitingForApprovalMutation,
	useApproveFormMutation,
	useRejectFormMutation,
	useSoftDeleteFormsByProfileIdMutation,
	useGetDifferenceCheckQuery,
	useGetLatestFormQuery,
	useGetFormFromTokenQuery
} = sustainabilityApi;

export default sustainabilityApi;
